/**
 * =============================================================================
 * APPLICATION ENTRY POINT
 * =============================================================================
 * 
 * Main entry point for the LGBT Resources Widget/Map application.
 * Sets up React rendering, routing, and authentication context.
 * 
 * Application Structure:
 * - React 18 with StrictMode enabled
 * - React Router v6 for client-side routing
 * - AuthContext provider for admin authentication
 * 
 * Available Routes:
 * - /           → Redirects to /widget
 * - /widget     → Widget demo and API key generator
 * - /map        → Map demo and API key generator
 * - /terms      → Terms of service
 * - /admin      → Analytics dashboard (protected)
 * - /admin/embed → Embed management (protected)
 * 
 * =============================================================================
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './index.css';

// =============================================================================
// COMPONENT IMPORTS
// =============================================================================

// Page Components
import App from './App';
import WidgetDemo from './WidgetDemo';
import MapDemo from './MapDemo';
import Terms from './components/Terms';

// Admin Components
import AnalyticsDashboard from './components/AnalyticsDashboard';
import AdminEmbed from './components/AdminEmbed';

// Context Providers
import { AuthProvider } from './contexts/AuthContext';

// =============================================================================
// APPLICATION INITIALIZATION
// =============================================================================

/**
 * Create the React root and render the application.
 * 
 * Structure:
 * - StrictMode: Enables additional development checks
 * - AuthProvider: Provides authentication context to all routes
 * - Router: Enables client-side routing
 * - Routes: Defines the application's route structure
 */
const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    {/* Authentication Context - Wraps entire app for admin functionality */}
    <AuthProvider>
      {/* Router - Enables client-side navigation */}
      <Router>
        <Routes>
          {/* 
           * Public Routes
           * These routes are accessible to all visitors
           */}
          <Route path="/" element={<App />} />
          <Route path="/widget" element={<WidgetDemo />} />
          <Route path="/map" element={<MapDemo />} /> 
          <Route path="/terms" element={<Terms />} />
          
          {/* 
           * Admin Routes
           * These routes require authentication (handled by AuthContext)
           */}
          <Route path="/admin" element={<AnalyticsDashboard />} />
          <Route path="/admin/embed" element={<AdminEmbed />} />
        </Routes>
      </Router>
    </AuthProvider> 
  </React.StrictMode>
);